import {
  convertColorToRGBAUnits,
  extractOpacity,
  applyOpacity,
  roundToTwoDecimals,
} from '../../../../utils/colorTransformations';
import { isHexaColor, isRGBAColor } from '../../../../utils/colorAssertions';
import type { CorvidSDKPropsFactory } from '@wix/editor-elements-types/corvid';
import { withValidation } from '../../validations';
import { createColorValidator } from './validation';
import { getScopedVar } from './styleUtils';
import { cssVars, styleStateCssVars } from './constants';
import type { IStylePropsSDKOptions, StyleState } from './types';
import { capitalize } from './capitalize';

export type ColorProperty = Exclude<
  keyof typeof cssVars,
  'borderRadius' | 'borderWidth'
>;

export type IColorSDK<P extends ColorProperty> = {
  [K in P]: string;
};

export type IColorWithStatesSDK<P extends ColorProperty> = IColorSDK<P> & {
  [K in StyleState]?: IColorSDK<P>;
};

type ColorWithStatesCorvidSDKPropsFactory<P extends ColorProperty> =
  CorvidSDKPropsFactory<
    IColorWithStatesSDK<P>,
    { [K in P]?: string | null } & {
      [K in StyleState]?: { [SK in P]?: string | null };
    }
  >;

export const buildColorPropsSDKFactoryCreator =
  <P extends ColorProperty>(propertyName: P) =>
  (
    options: IStylePropsSDKOptions = { supportOpacity: true },
  ): ColorWithStatesCorvidSDKPropsFactory<P> => {
    const {
      prefix,
      supportOpacity,
      withoutDefaultValue,
      supportedStates = [],
    } = options;

    const _createColorPropsSDKFactory = (
      cssRule: string,
      statePropertyName: string = propertyName,
    ) => {
      const validateColor = createColorValidator({
        propertyName,
        cssProperty: supportOpacity ? 'rgbaColor' : 'rgbColor',
        supportAlpha: supportOpacity,
      });

      const _colorPropsSDKFactory: CorvidSDKPropsFactory<
        IColorSDK<P>,
        Partial<IColorSDK<P>>
      > = ({ setStyles, sdkData, createSdkState }) => {
        const editorInitialColor =
          sdkData?.initialSdkStyles?.[statePropertyName];
        const editorOpacity = extractOpacity(editorInitialColor);

        const initialValue = withoutDefaultValue
          ? undefined
          : editorInitialColor;
        const [state, setState] = createSdkState(
          { [statePropertyName]: initialValue },
          statePropertyName,
        );

        return Object.defineProperty<object>(
          {
            reset() {
              setState({ [statePropertyName]: initialValue });
              setStyles({ [cssRule]: undefined });
            },
          },
          propertyName,
          {
            enumerable: true,
            set(value) {
              let colorValue = value;

              /**
               * !Alert! This feature is intended.
               * if mixin does not support opacity - cast it to RGB
               */
              if (
                !supportOpacity &&
                (isHexaColor(value) || isRGBAColor(value))
              ) {
                const [r, g, b] = convertColorToRGBAUnits(value)!;
                colorValue = `rgb(${r}, ${g}, ${b})`;
              }

              /**
               * !Alert! This feature is intended.
               *  Editor color alpha gets modified by the amount of user color alpha
               */
              if (typeof editorOpacity === 'number' && editorOpacity !== 1) {
                const userOpacity = extractOpacity(value);
                const opacity = Number.isFinite(userOpacity)
                  ? roundToTwoDecimals(editorOpacity * (userOpacity as number))
                  : editorOpacity;
                colorValue = applyOpacity(colorValue, opacity);
              }

              setState({ [statePropertyName]: colorValue });
              setStyles({ [cssRule]: colorValue });
            },
            get() {
              return state[statePropertyName];
            },
          },
        );
      };

      return withValidation(
        _colorPropsSDKFactory,
        {
          type: ['object'],
          properties: {
            [propertyName]: {
              type: ['string', 'nil'],
            },
          },
        },
        {
          [propertyName]: [validateColor],
        } as object,
      );
    };

    const styleStateSDKFactories = supportedStates.map(styleState => {
      const _createStyleStateFactory: CorvidSDKPropsFactory<
        IColorSDK<P>,
        { [K in StyleState]?: Partial<IColorSDK<P>> }
      > = api => {
        const styleStateSDK = _createColorPropsSDKFactory(
          getScopedVar({
            name: styleStateCssVars[styleState][propertyName],
            prefix,
          }),
          styleState + capitalize(propertyName),
        )(api);

        return {
          [styleState]: styleStateSDK,
        };
      };

      return withValidation(_createStyleStateFactory, {
        type: ['object'],
        properties: {
          [styleState]: {
            type: ['object', 'nil'],
          },
        },
      });
    });

    const regularSDKFactory = _createColorPropsSDKFactory(
      getScopedVar({ name: cssVars[propertyName], prefix }),
    );

    return api =>
      Object.assign(
        regularSDKFactory(api),
        styleStateSDKFactories.reduce(
          (sdk, styleStateSDKFactory) =>
            Object.assign(sdk, styleStateSDKFactory(api)),
          {},
        ),
      );
  };
